import type {
  VoiceAgentSummary,
  VoiceAgentsResponse,
  VoiceCallDetail,
  VoiceCallSummary,
  VoiceCallsResponse,
  VoiceConfigurationState,
  StructuredOutputRecord,
  SharedRecordNote,
  SharedCallTriageChange,
} from '@oneup/shared';
import { apiUrl, authorizedFetch, request } from './api';
import type { IntakeLifecycle } from './leadloop-api';

export { VOICE_CASCADE_NOT_CONFIGURED_MESSAGE } from '@oneup/shared';

export interface AiAgentItem {
  id: string;
  name: string;
  assignedClient?: string;
  role?: string;
  type?: string;
  status?: string;
  connectionStatus?: string;
  lastModified?: string;
  recentCallCount: number;
  lastCallAt?: string;
  followUpCount: number;
  errorCount: number;
  source: 'canonical_voice';
}

export interface AiCallItem {
  id: string;
  callerName?: string;
  businessName?: string;
  phone?: string;
  email?: string;
  direction?: string;
  type?: string;
  status?: string;
  outcome?: string;
  agentName?: string;
  agentRecordIds: string[];
  startedAt?: string;
  endedAt?: string;
  durationSeconds?: number;
  summary?: string;
  transcript?: string;
  recordingUrl?: string;
  reasonForCall?: string;
  urgency?: string;
  followUpRequired: boolean;
  spam: boolean;
  // Analysis surfaced by the shared call views.
  leadGrade?: string;
  sourceRating?: number;
  leadScore?: number;
  assistantScore?: number;
  sentiment?: string;
  callCategory?: string;
  callCategoryLabel?: string;
  callerRelationship?: string;
  callerRelationshipLabel?: string;
  leadEligibility?: string;
  leadEligibilityLabel?: string;
  triageReasoning?: string;
  isLowValue?: boolean;
  archived?: boolean;
  archivedAt?: string;
  notes?: SharedRecordNote[];
  triageHistory?: SharedCallTriageChange[];
  gradeExplanation?: string;
  structuredOutputs?: StructuredOutputRecord[];
  leadRecordIds: string[];
  contactRecordIds: string[];
  source: 'canonical_voice';
  /**
   * Where this call stands in the intake lifecycle, joined server-side by the
   * Cascade route. Optional: the shared voice service is provider-neutral and
   * knows nothing about intake, so a portal without that layer omits it.
   */
  lifecycle?: IntakeLifecycle;
  /** "Lead Created" / "Linked to Lead" / "Dismissed". Absent while open. */
  resolutionLabel?: string;
}

export interface VoiceManagementTemplate {
  id: string;
  name: string;
  currentVersion: { id: string } | null;
}

export interface VoiceManagementProviderAccount {
  id: string;
  provider: string;
  account_type: string;
}

export interface VoiceManagementProviderAssistant {
  providerResourceId: string;
  name: string | null;
}

export interface VoiceManagedAgent {
  id: string;
  name: string;
  operational_role: string;
  lifecycle_status: 'draft' | 'published' | 'archived';
  current_draft_version_id: string | null;
  published_version_id: string | null;
  currentDraft: { id: string; provider_account_id: string | null; version_number: number } | null;
}

export interface VoiceManagementReadiness {
  clientId: string;
  enabled: boolean;
  ready: boolean;
  configurationReady?: boolean;
  liveCallReady?: boolean;
  checks?: Partial<{
    verifiedIdentity: boolean;
    providerAccountActive: boolean;
    providerCredentialAvailable: boolean;
    providerReachable: boolean;
    providerIdentityMatches: boolean;
    entitlementConfigured: boolean;
  }>;
  liveCallChecks?: Partial<{
    webhookUrlConfigured: boolean;
    webhookAuthenticationConfigured: boolean;
    phoneAllocated: boolean;
    successfulWebhookReceived: boolean;
  }>;
  provider?: {
    accountId: string | null;
    errorCode: string | null;
  };
}

interface VoiceManagementEnvelope<T> {
  data: T;
  requestId: string;
}

interface AiSystemsListResponse<T> {
  items: T[];
  total: number;
  warnings: string[];
  source: { canonicalApi: '/api/voice'; clientId: 'cascade' };
  nextCursor?: string | null;
  configuration?: VoiceConfigurationState;
}

interface CascadeCallsResponse extends VoiceCallsResponse {
  configuration?: VoiceConfigurationState;
}

export function withQuery(path: string, params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    // "all" is a meaningful value for the Calls API (`view=all`). Generic
    // query construction must not silently reinterpret it as "unset".
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

function adaptAgent(item: VoiceAgentSummary): AiAgentItem {
  return {
    id: item.id,
    name: item.displayName,
    assignedClient: 'Cascade',
    role: item.purpose,
    type: 'Voice Agent',
    status: item.status,
    connectionStatus: item.mapped && item.phoneMapped ? 'Connected' : 'Configuration required',
    lastModified: item.updatedAt,
    recentCallCount: item.metrics.recentCallCount,
    lastCallAt: item.metrics.lastCallAt,
    followUpCount: item.metrics.followUpCount,
    errorCount: item.metrics.issueCount,
    source: 'canonical_voice',
  };
}

function structuredString(value: unknown, ...keys: string[]): string | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  for (const key of keys) {
    const candidate = record[key];
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return undefined;
}

function adaptCall(item: VoiceCallSummary | VoiceCallDetail): AiCallItem {
  const detail = 'structuredIntake' in item ? item : undefined;
  return {
    id: item.id,
    callerName: item.caller.name,
    businessName: item.caller.businessName,
    phone: item.caller.phone,
    email: item.caller.email,
    direction: item.direction,
    type: item.direction,
    status: item.status,
    outcome: item.outcome,
    agentName: item.agentName,
    agentRecordIds: item.agentId ? [item.agentId] : [],
    startedAt: item.startedAt,
    endedAt: item.endedAt,
    durationSeconds: item.durationSeconds,
    summary: item.summary,
    transcript: detail?.transcript,
    recordingUrl: detail?.recordingUrl ? apiUrl(detail.recordingUrl) : undefined,
    reasonForCall: structuredString(detail?.structuredIntake, 'reason_for_call', 'reason'),
    urgency: structuredString(detail?.structuredIntake, 'urgency'),
    followUpRequired: item.followUpRequired,
    spam: false,
    leadGrade: item.leadGrade,
    sourceRating: item.sourceRating,
    leadScore: item.leadScore,
    assistantScore: item.assistantScore,
    sentiment: item.sentiment,
    callCategory: item.callCategory,
    callCategoryLabel: item.callCategoryLabel,
    callerRelationship: item.callerRelationship,
    callerRelationshipLabel: item.callerRelationshipLabel,
    leadEligibility: item.leadEligibility,
    leadEligibilityLabel: item.leadEligibilityLabel,
    triageReasoning: item.triageReasoning,
    isLowValue: item.isLowValue,
    archived: item.archived,
    archivedAt: item.archivedAt,
    notes: detail?.notes,
    triageHistory: detail?.triageHistory,
    gradeExplanation: detail?.gradeExplanation,
    structuredOutputs: detail?.structuredOutputs,
    leadRecordIds: [],
    contactRecordIds: [],
    source: 'canonical_voice',
    // Joined onto the payload by the Cascade route, so it is not on the shared
    // VoiceCallSummary type. Absent on any portal without an intake layer.
    lifecycle: (item as { lifecycle?: IntakeLifecycle }).lifecycle,
    resolutionLabel: (item as { resolutionLabel?: string }).resolutionLabel,
  };
}

export const aiSystemsApi = {
  agents: async (q?: string): Promise<AiSystemsListResponse<AiAgentItem>> => {
    const response = await request<VoiceAgentsResponse>(withQuery('/api/voice/agents', { search: q, limit: 100 }));
    return {
      items: response.items.map(adaptAgent),
      total: response.total,
      warnings: response.configuration ? [response.configuration.message] : [],
      source: { canonicalApi: '/api/voice', clientId: 'cascade' },
      configuration: response.configuration,
    };
  },
  calls: async (params: { q?: string; view?: string; category?: string; showArchived?: boolean; limit?: number; dateFrom?: string; dateTo?: string; cursor?: string } = {}): Promise<AiSystemsListResponse<AiCallItem>> => {
    const response = await request<CascadeCallsResponse>(withQuery('/api/voice/calls', {
      search: params.q,
      category: params.category,
      show_archived: params.showArchived,
      date_from: params.dateFrom,
      date_to: params.dateTo,
      limit: Math.min(params.limit ?? 100, 100),
      view: params.view,
      cursor: params.cursor,
    }));
    const items = response.items.map(adaptCall);
    return {
      items,
      total: items.length,
      warnings: response.configuration ? [response.configuration.message] : [],
      source: { canonicalApi: '/api/voice', clientId: 'cascade' },
      nextCursor: response.nextCursor,
      configuration: response.configuration,
    };
  },
  call: async (id: string): Promise<AiCallItem> => {
    const response = await request<{ item: VoiceCallDetail }>(`/api/voice/calls/${encodeURIComponent(id)}`);
    return adaptCall(response.item);
  },
  updateTriage: (id: string, body: { callerRelationship: string; callPurpose: string; leadEligibility: string }) =>
    request(`/api/voice/calls/${encodeURIComponent(id)}/triage`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  addNote: (id: string, body: string) => request(`/api/voice/calls/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  }),
  archive: (id: string, archived: boolean) => request(`/api/voice/calls/${encodeURIComponent(id)}/${archived ? 'archive' : 'restore'}`, {
    method: 'POST',
  }),
  openRecording: async (recordingUrl: string): Promise<string> => {
    const response = await authorizedFetch(recordingUrl, { headers: { Accept: 'audio/*' } });
    if (!response.ok) throw new Error(`Recording request failed (${response.status}).`);
    return URL.createObjectURL(await response.blob());
  },
};

function voiceMutation<T>(path: string, body: Record<string, unknown>) {
  return request<VoiceManagementEnvelope<T>>(path, {
    method: 'POST',
    headers: { 'Idempotency-Key': crypto.randomUUID() },
    body: JSON.stringify(body),
  }).then((response) => response.data);
}

export const voiceManagementApi = {
  readiness: () => request<VoiceManagementEnvelope<VoiceManagementReadiness>>('/api/voice/management-readiness').then((response) => response.data),
  templates: () => request<VoiceManagementEnvelope<VoiceManagementTemplate[]>>('/api/voice/agent-templates').then((response) => response.data),
  providerAccounts: () => request<VoiceManagementEnvelope<VoiceManagementProviderAccount[]>>('/api/voice/provider/accounts').then((response) => response.data),
  providerAssistants: (providerAccountId: string) => request<VoiceManagementEnvelope<{ items: VoiceManagementProviderAssistant[] }>>(
    `/api/voice/provider/assistants?providerAccountId=${encodeURIComponent(providerAccountId)}`,
  ).then((response) => response.data.items),
  managedAgents: () => request<VoiceManagementEnvelope<VoiceManagedAgent[]>>('/api/voice/managed-agents').then((response) => response.data),
  importAgent: (body: { providerAccountId: string; providerAssistantId: string; displayName?: string }) =>
    voiceMutation<Record<string, unknown>>('/api/voice/agents/import', body),
  createAgent: (body: { providerAccountId: string; templateVersionId: string; name: string }) =>
    voiceMutation<Record<string, unknown>>('/api/voice/agents', {
      ...body,
      operationalRole: 'receptionist',
      managedConfig: {},
    }),
  publishAgent: (agentId: string, draftVersionId: string, providerAccountId: string) =>
    voiceMutation<Record<string, unknown>>(`/api/voice/agents/${encodeURIComponent(agentId)}/publish`, {
      draftVersionId,
      providerAccountId,
      expectedProviderHash: null,
    }),
};
